import { useState } from "react";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { DotLegalBreadCrumbModel, useDotLegalSnackbar, useHistoryWithReferer } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { SelectableItem } from "../../processingActivity/ProcessingActivity.types";
import { useUrlProvider } from "../../useUrlProvider";
import { allUserColumns, UserColumn, UserExportHeader, UserRowTableModel, UsersSavedFilter, UsersViewModel, UserViewModel } from "../User.types";
import { FilterOption, FilterOptionType, UserFieldGrouping } from "../../common/components/filterPopover/FilterPopover.types";
import { applyClientFilters, hasDateRange, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { DateFieldValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { GetDateData, GetListData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { FilterOperatorUi } from "@common/api/filterTypes";
import { FilterSortState } from "@common/hooks/useFilterState";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { usePageView } from "@common/pageView/PageView.hooks";
import { FilterPage, SessionStorageSavedView } from "@common/pageView/PageView.types";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types";
import { useCustomListExport } from "../../customLists/customListView/useCustomListExport";
import { getDisplayedRows } from "@common/tableSearch";

import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";

const businessAreasFilterId = "businessAreas";
const rolesFilterId = "roles";
const activeFilterId = "active";
const lastLoginFilterId = "lastLogin";
const createdFilterId = "created";

// Each column's header label and the row property the table sorts it on. The two differ wherever the cell renders
// something other than a plain field, so this is the single place a column's sort target is defined.
const sortPropertyByColumn: Record<UserColumn, keyof UserRowTableModel> = {
    [UserColumn.Name]: "firstName",
    [UserColumn.EmailAddress]: "emailAddress",
    [UserColumn.BusinessAreas]: "businessAreas",
    [UserColumn.Roles]: "userRoles",
    [UserColumn.GroupEntities]: "gdpo",
    [UserColumn.LastLogin]: "lastLogin",
    [UserColumn.Created]: "created",
    [UserColumn.Status]: "isActive",
};

// Must deep-equal the backend strategy's GetDefaultFilterData after a JSON round-trip, or the system default view
// would be flagged as changed before the user touches anything. Deactivated users are hidden by default;
// explicitly clearing the status filter (no active key) means "show all".
const defaultUsersFilter: UsersSavedFilter = {
    selectFilters: { [activeFilterId]: ["true"] },
    dateFilters: {},
    filterOperators: {},
};

function isUserColumn(column: string): column is UserColumn {
    return allUserColumns.includes(column as UserColumn);
}

export function useUsersDataMapping() {
    const queryClient = useDotLegalQueryClient();
    const historyWithReferer = useHistoryWithReferer();
    const urlProvider = useUrlProvider();
    const snackbar = useDotLegalSnackbar();

    const { translateString, translateDate } = useTranslation();
    const { exportUsersInBackground } = useCustomListExport();

    const [showAddUserDialog, setShowAddUserDialog] = useState(false);
    const [searchString, setSearchString] = useState("");

    const savedViewQuery = useSavedViewQuery(FilterPage.Users);
    const {
        currentViewId,
        tableSessionStateKey,
        getFilterData,
        getDisplayData,
        setFilterData,
        setDisplayData,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged,
        isSavedView,
        savedView,
        setView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView<UsersSavedFilter, Array<UserColumn>>(FilterPage.Users, savedViewQuery.data);

    const filterData = getFilterData<UsersSavedFilter>() ?? defaultUsersFilter;
    const { selectFilters, filterOperators } = filterData;
    // Views persisted before the last-login filter existed carry no dateFilters key, so indexing the raw value
    // would throw on the first render of an older saved view.
    const dateFilters = filterData.dateFilters ?? {};
    // The backend system-default view supplies the default columns once saved views resolve; until then the table
    // is in its loading state, so an empty fallback never renders.
    const visibleColumns = getDisplayData<Array<UserColumn>>() ?? [];

    const url = `/Users`;
    const { isLoading, data } = useDotLegalQuery<UsersViewModel>(url);

    const hasUsers = isLoading ? true : (data?.users.length ?? 0) > 0;

    const columnLabels: Record<UserColumn, string> = {
        [UserColumn.Name]: translateString("name"),
        [UserColumn.EmailAddress]: translateString("emailAddress"),
        [UserColumn.BusinessAreas]: translateString("processingActivityAreas"),
        [UserColumn.Roles]: translateString("roles"),
        [UserColumn.GroupEntities]: translateString("groupEntities"),
        [UserColumn.LastLogin]: translateString("lastLogin"),
        [UserColumn.Created]: translateString("created"),
        [UserColumn.Status]: translateString("userStatus"),
    };

    const tableFields: Array<TableFields> = allUserColumns.map((column) => ({
        id: column,
        name: columnLabels[column],
        grouping: UserFieldGrouping.User,
    }));

    // A saved view persists raw column ids, so one written before a column was removed would still list it. Drop
    // anything no longer in the vocabulary rather than rendering a header with no cell under it.
    const renderedColumns = visibleColumns.filter(isUserColumn);

    // Header text plus the row property each column sorts on, in on-screen order.
    const tableHeaders = renderedColumns.map((column) => ({ property: sortPropertyByColumn[column], text: columnLabels[column] }));

    // Options for filters whose values come from the loaded users, dropping selections that no user carries
    // anymore so a stale saved view does not filter everything away silently.
    const businessAreaIds = (selectFilters[businessAreasFilterId] ?? []).filter((id) =>
        (data?.users ?? []).some((u) => u.businessAreas.some((x) => x.id === id))
    );
    const roleIds = (selectFilters[rolesFilterId] ?? []).filter((id) => (data?.users ?? []).some((u) => u.userRoles.some((x) => x.id === id)));
    const isActive = selectFilters[activeFilterId] ?? [];

    const users = applyClientFilters(
        data?.users ?? [],
        [
            { id: businessAreasFilterId, candidate: (u) => u.businessAreas.map((x) => x.id) },
            { id: rolesFilterId, candidate: (u) => u.userRoles.map((x) => x.id) },
            { id: activeFilterId, candidate: (u) => u.isActive.toString() },
        ],
        { ...selectFilters, [businessAreasFilterId]: businessAreaIds, [rolesFilterId]: roleIds, [activeFilterId]: isActive },
        {},
        filterOperators,
        (u) => matchesDateFilter(dateFilters[lastLoginFilterId], u.lastLogin) && matchesDateFilter(dateFilters[createdFilterId], u.created)
    )!;

    const userRows = formatDataToTable(users);

    // The table's current sort, falling back to the name column until the user sorts a column. Shared by the table
    // config (defaultOrderBy/defaultOrder) and the export so both order rows identically.
    // A persisted sort is a raw row property, so it is narrowed here rather than at the table.
    const sortOrderBy = (filterData.sortByColumn as keyof UserRowTableModel | undefined) ?? sortPropertyByColumn[UserColumn.Name];
    const sortOrder = filterData.sortDirection ?? "asc";

    return {
        pinnedFilterIds,
        setPinnedFilterIds,
        getBreadCrumbs,
        isLoading,
        showAddUserDialog,
        setShowAddUserDialog,
        handleUserAddSuccess,
        goToUser,
        hasUsers,
        getFilterDataOptions,
        onFilterChange,
        clearFilterData,
        searchString,
        setSearchString,
        users: userRows,
        tableFields,
        visibleColumns,
        renderedColumns,
        tableHeaders,
        onColumnsSave,
        savedView,
        currentViewId,
        tableSessionStateKey,
        isSavedView,
        hasFilterChanged,
        hasDisplayChanged,
        filterData,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        sortOrderBy,
        sortOrder,
        onSortChange,
        onDownloadExcelReportClick,
    };

    function getBreadCrumbs() {
        let breadCrumbs: Array<DotLegalBreadCrumbModel> = [];

        breadCrumbs.push({ name: translateString("users") });

        return breadCrumbs;
    }

    function handleUserAddSuccess() {
        queryClient.refetchQueries(url);
        setShowAddUserDialog(false);
    }

    function formatDataToTable(data: Array<UserViewModel>): Array<UserRowTableModel> {
        return data.map((x) => {
            return {
                ...x,
                lastLoginString: x.lastLogin ? translateDate(x.lastLogin) : "",
                createdString: translateDate(x.created),
                isActiveString: x.isActive ? translateString("active") : translateString("deactivated"),
            };
        });
    }

    function getFilterDataOptions() {
        if (!data) {
            return [];
        }

        return withOperators(
            [
                {
                    type: FilterOptionType.SelectField,
                    id: businessAreasFilterId,
                    value: businessAreaIds,
                    label: translateString("businessAreas"),
                    fixedData: createSelectOptions(data.users.flatMap((x) => x.businessAreas)),
                },
                {
                    type: FilterOptionType.SelectField,
                    id: rolesFilterId,
                    value: roleIds,
                    label: translateString("roles"),
                    fixedData: createSelectOptions(data.users.flatMap((x) => x.userRoles)),
                },
                {
                    type: FilterOptionType.SelectField,
                    id: activeFilterId,
                    value: isActive,
                    label: translateString("userStatus"),
                    fixedData: [
                        { id: "true", name: translateString("active") },
                        { id: "false", name: translateString("deactivated") },
                    ],
                },
                {
                    type: FilterOptionType.DateField,
                    id: lastLoginFilterId,
                    value: dateFilters[lastLoginFilterId] ?? "",
                    label: translateString("lastLogin"),
                    // A last login is always in the past. The "no date" option is deliberately kept: it is how the
                    // user filters for accounts that have never logged in.
                    hideFutureDates: true,
                },
                {
                    type: FilterOptionType.DateField,
                    id: createdFilterId,
                    value: dateFilters[createdFilterId] ?? "",
                    label: translateString("created"),
                    // Every user has a created date and it is always in the past, so both a future range and the
                    // "no date" option could only ever match nothing.
                    hideFutureDates: true,
                    hideNoDate: true,
                },
            ],
            filterOperators
        );
    }

    // Cleared filters are dropped (no empty arrays, no orphaned operators) so the persisted shape deep-equals a
    // saved view's JSON round-trip — otherwise change detection would flag phantom differences.
    function onFilterChange(updatedOptions: Array<FilterOption>) {
        const nextSelect: Record<string, Array<string>> = {};
        const nextDate: Record<string, DateFieldValue> = {};
        const nextOperators: Record<string, FilterOperatorUi> = {};

        updatedOptions.forEach((option) => {
            if (option.type === FilterOptionType.SelectField) {
                const values = GetListData(option.value);
                if (values.length > 0) {
                    nextSelect[option.id] = values;
                    if (option.filterOperator !== undefined) {
                        nextOperators[option.id] = option.filterOperator;
                    }
                }
            }
            if (option.type === FilterOptionType.DateField) {
                const dateValue = GetDateData(option.value);
                if (hasDateRange(dateValue)) {
                    nextDate[option.id] = dateValue;
                }
            }
        });

        setFilterData({ ...filterData, selectFilters: nextSelect, dateFilters: nextDate, filterOperators: nextOperators });
    }

    function onSortChange(next: FilterSortState) {
        setFilterData({ ...filterData, sortByColumn: next.orderBy, sortDirection: next.order });
    }

    function onColumnsSave(columns: Array<string>) {
        setDisplayData(columns.filter(isUserColumn));
    }

    function applyView(viewId: string | undefined, filter: UsersSavedFilter, display: Array<UserColumn>) {
        const updatedView: SessionStorageSavedView = {
            page: FilterPage.Users,
            viewId,
            filterData: filter,
            displayData: display,
        };
        setView(updatedView);
    }

    function onViewSaved(viewId: string) {
        applyView(viewId, filterData, visibleColumns);
    }

    function onViewDeleted(deletedViewId: string) {
        if (deletedViewId !== currentViewId) return;

        const fallbackView = savedViewQuery.data?.find((x: SavedView) => x.isCustomerDefault || x.isSystemDefault);
        applyView(fallbackView?.id === deletedViewId ? undefined : fallbackView?.id, filterData, visibleColumns);
    }

    function onChangeView(view: SavedView) {
        if (view.id !== currentViewId) {
            applyView(view.id, view.filterData, view.displayData);
        }
    }

    // Filtering/sort/search are client-side here, so the export mirrors the table exactly: it sends the on-screen
    // rows (filter bar + search box + current sort) in order and the backend re-fetches and formats the values.
    async function onDownloadExcelReportClick() {
        const headers: Array<UserExportHeader> = renderedColumns.map((column) => ({ header: columnLabels[column], column }));

        const rows = getDisplayedRows(userRows, searchString, sortOrderBy, sortOrder).map((row) => row.id);

        // The filename is uncapped, so only it also carries the active view, which distinguishes two exports of the
        // same page. The report name doubles as the Excel sheet tab, whose naming rules the generator enforces.
        const reportName = translateString("users");
        const fileNameBase = `${reportName} - ${savedView!.name}`;

        await exportUsersInBackground(reportName, headers, rows, fileNameBase);

        snackbar.show(translateString("usersDataExportInProgress"), "success", true);
    }

    function goToUser(userId: string) {
        historyWithReferer.push(urlProvider.getUserUrl(userId));
    }

    // Uses Map with id as key to return distinct options
    function createSelectOptions(items: Array<SelectableItem>): Array<DotLegalSelectOption> {
        return [...new Map(items.map((x) => [x.id, { name: x.name, id: x.id }])).values()];
    }
}
